/**
 * Data Fetch Strategy Interface
 * Base interface for all data fetching strategies
 */

import { Observable } from 'rxjs';
import { IDataFetchContext } from '../models';

/**
 * Base interface that all data fetch strategies must implement
 */
export interface IDataFetchStrategy {
  /**
   * Determines if this strategy can handle the given data fetch type
   * @param dataFetchType The type of data fetching (api, patch, jsexecutor, etc.)
   * @returns true if this strategy can handle the type
   */
  canHandle(dataFetchType: string): boolean;
  
  /**
   * Executes the data fetching logic
   * @param context The execution context containing field, form, and user info
   * @returns Observable of the fetched data
   */
  execute(context: IDataFetchContext): Observable<any>;
  
  /**
   * Returns a human-readable name for the strategy
   * @returns Strategy name for logging and debugging
   */
  getName(): string;
  
  /**
   * Optional: Setup logic when field is initialized
   * Use this to subscribe to dependent field changes, set up listeners, etc.
   * @param context The execution context
   */
  setup?(context: IDataFetchContext): void;
  
  /**
   * Optional: Cleanup logic when field is destroyed
   * Use this to unsubscribe, clean up resources, etc.
   * @param context The execution context
   */
  cleanup?(context: IDataFetchContext): void;
  
  /**
   * Optional: Validate if the strategy can execute with the given context
   * @param context The execution context
   * @returns true if context is valid for execution
   */
  validate?(context: IDataFetchContext): boolean;
}

/**
 * Abstract base class for data fetch strategies
 * Provides common functionality and error handling
 */
export abstract class BaseDataFetchStrategy implements IDataFetchStrategy {
  /**
   * Must be implemented by concrete strategies
   */
  abstract canHandle(dataFetchType: string): boolean;
  
  /**
   * Must be implemented by concrete strategies
   */
  abstract execute(context: IDataFetchContext): Observable<any>;
  
  /**
   * Must be implemented by concrete strategies
   */
  abstract getName(): string;
  
  /**
   * Default validation - checks if field has required configuration
   */
  validate(context: IDataFetchContext): boolean {
    if (!context.field) {
      this.logWarning('No field provided in context', context);
      return false;
    }
    
    if (!context.formGroup) {
      this.logWarning('No form group provided in context', context);
      return false;
    }
    
    return true;
  }
  
  /**
   * Log an error with strategy context
   */
  protected logError(error: any, context: IDataFetchContext): void {
    console.error(
      `[${this.getName()}] Error for field "${context.field.dbFeildName}":`,
      error
    );
  }
  
  /**
   * Log a warning with strategy context
   */
  protected logWarning(message: string, context: IDataFetchContext): void {
    console.warn(
      `[${this.getName()}] Warning for field "${context.field.dbFeildName}": ${message}`
    );
  }
  
  /**
   * Log info with strategy context
   */
  protected logInfo(message: string, context: IDataFetchContext): void {
    console.log(
      `[${this.getName()}] ${message} for field "${context.field.dbFeildName}"`
    );
  }
  
  /**
   * Check if field has dependent value when required
   */
  protected hasDependentValue(context: IDataFetchContext): boolean {
    if (!context.field.dependentOn) {
      return true; // No dependency required
    }
    
    const value = context.formGroup.get(context.field.dependentOn)?.value;
    return value !== null && value !== undefined && value !== '';
  }
  
  /**
   * Get dependent field value
   * ✅ Enhanced to support dependentOnKey
   */
  protected getDependentValue(context: IDataFetchContext): any {
    if (!context.field.dependentOn) {
      return null;
    }
    
    // ✅ NEW: Handle dependentOnKey if specified
    if (context.field.dependentOnKey) {
      // Get the selected value from dependentOn field
      const selectedValue = context.formGroup.get(context.field.dependentOn)?.value;
      
      // Find the parent field config to get its data
      const parentField = context.allFields?.find(field => field.dbFeildName === context.field.dependentOn);
      
      if (parentField && parentField.options && selectedValue) {
        // Find the selected item in parent field's data
        const selectedItem = parentField.options.find((item: any) => {
          // Match by patchPropertyName (the value field)
          const patchKey = Object.keys(item).find(key => 
            key.toLowerCase() === parentField.patchPropertyName?.toLowerCase()
          );
          return patchKey && item[patchKey] === selectedValue;
        });
        
        if (selectedItem) {
          // Extract the dependentOnKey value from the selected item
          const dependentKey = Object.keys(selectedItem).find(key => 
            key.toLowerCase() === context.field.dependentOnKey?.toLowerCase()
          );
          return dependentKey ? selectedItem[dependentKey] : selectedValue;
        } else {
          return selectedValue;
        }
      } else {
        // Fallback to getting value directly from dependentOnKey field
        return context.formGroup.get(context.field.dependentOnKey)?.value;
      }
    } else {
      // OLD WAY: No dependentOnKey specified, use dependentOn field value directly
      return context.formGroup.get(context.field.dependentOn)?.value;
    }
  }
  
  /**
   * Replace placeholders in URL/query - LEGACY COMPATIBLE
   * Replaces all placeholders supported by legacy formatAPIURL method
   */
  protected replacePlaceholders(
    template: string,
    context: IDataFetchContext
  ): string {
    let result = template;
    
    // Replace CompanyId and LoggedInCompanyId (same value)
    result = result.replace(/\{\{CompanyId\}\}/gi, context.companyId?.toString() || '');
    result = result.replace(/\{\{LoggedInCompanyId\}\}/gi, context.companyId?.toString() || '');
    
    // Replace BranchId
    result = result.replace(/\{\{BranchId\}\}/gi, context.branchId?.toString() || '');
    
    // Replace UserId
    result = result.replace(/\{\{UserId\}\}/gi, context.userId?.toString() || '');
    
    // Replace MasterId
    result = result.replace(/\{\{MasterId\}\}/gi, context.masterId?.toString() || '0');
    
    // Replace SoftwareId (from environment)
    const softwareId = (context as any).softwareId || '1';
    result = result.replace(/\{\{SoftwareId\}\}/gi, softwareId?.toString() || '1');
    
    // Replace urlId (from route params if available)
    const urlId = (context as any).urlId || '0';
    result = result.replace(/\{\{urlId\}\}/gi, urlId?.toString() || '0');
    
    // Replace form field values: hLevel, itemId, SelectedBranchId
    const hLevel = context.formGroup.get('hLevel')?.value || 0;
    result = result.replace(/\{\{hLevel\}\}/gi, hLevel?.toString() || '0');
    
    const itemId = context.formGroup.get('itemId')?.value || 0;
    result = result.replace(/\{\{itemId\}\}/gi, itemId?.toString() || '0');
    
    const selectedBranchId = context.formGroup.get('branchId')?.value ?? context.branchId;
    result = result.replace(/\{\{SelectedBranchId\}\}/gi, selectedBranchId?.toString() || '');
    
    // Replace DependentOnId (value of dependent field)
    const dependentValue = this.getDependentValue(context);
    if (dependentValue !== null && dependentValue !== undefined && dependentValue !== '') {
      const depValue = typeof dependentValue === 'object' 
        ? dependentValue[context.field.displayPropertyName || 'id'] || dependentValue.id || ''
        : dependentValue;
      result = result.replace(/\{\{DependentOnId\}\}/gi, depValue?.toString() || '0');
    } else {
      result = result.replace(/\{\{DependentOnId\}\}/gi, '0');
    }
    
    // Replace FilterValue (search term) - encode for URL safety
    const filterValue = context.searchTerm ? encodeURIComponent(context.searchTerm) : '';
    result = result.replace(/\{\{FilterValue\}\}/gi, filterValue);
    
    return result;
  }
}

/**
 * Strategy priority levels (for ordering when multiple strategies could apply)
 */
export enum StrategyPriority {
  Highest = 1,
  High = 2,
  Normal = 3,
  Low = 4,
  Lowest = 5,
}
